import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { isEqual } from 'lodash';

import { useAlert } from 'contexts/alert/useAlert';
import { StructureGroup, StructureGroupConfig } from 'data/generated/graphql';
import { useLayoutPublish } from 'features/layouts/hooks/useLayoutPublish';
import { deriveModules, EditorLayout, FlatSlot, groupModulesByGroupId, validateLayout } from 'utils/layoutUtils';
import { findTreatmentSetting } from 'utils/modules';
import { LayoutState, structureReducer } from './structureReducer';
import { TreatmentTypeSetting, useTreatmentTypeSettings } from './useTreatmentTypeSettings';

export type { TreatmentTypeSetting };

export interface StructureEditContextValue {
  layout: EditorLayout;
  /** Flat slot list derived from layout.structure.groups. */
  modules: FlatSlot[];
  treatmentTypeSettings: TreatmentTypeSetting[];
  isPublishing: boolean;
  hasUnsavedChanges: boolean;
  isReadOnly: boolean;
  updateLastSavedState: () => void;
  updateModule: (moduleIndex: number, updatedModule: FlatSlot) => void;
  reorderModules: (newModules: FlatSlot[]) => void;
  addModule: (module: FlatSlot, groupId: string | null) => void;
  removeModule: (moduleIndex: number) => void;
  restoreFromHistory: (historicalLayout: EditorLayout) => void;
  updateLayoutName: (name: string) => void;
  updateContentTypes: (contentTypes: string[]) => void;
  addGroup: (label: string, insertAfterGroupId?: string | null) => void;
  updateGroup: (groupId: string, patch: Partial<StructureGroupConfig>) => void;
  removeGroup: (groupId: string) => void;
  reorderGroups: (newGroups: StructureGroup[]) => void;
  handlePublish: () => Promise<void>;
  getAvailableModulesForGroup: (groupId: string | null) => { treatmentType: string; label: string }[];
  addNewModule: (groupId: string | null, treatmentType: string, localInsertIndex?: number) => void;
}

export const StructureEditContext = createContext<StructureEditContextValue | undefined>(undefined);

export const useStructureEditContext = (): StructureEditContextValue => {
  const context = useContext(StructureEditContext);
  if (!context) {
    throw new Error('useStructureEditContext must be used within StructureEditProvider');
  }
  return context;
};

interface StructureEditProviderProps {
  children: ReactNode;
  publicationKey: string;
  artifactId: string;
  initialLayout: EditorLayout;
  isReadOnly?: boolean;
}

export const StructureEditProvider = ({
  children,
  publicationKey,
  artifactId,
  initialLayout,
  isReadOnly = false
}: StructureEditProviderProps) => {
  const [{ layout, lastSavedLayout }, dispatch] = useReducer(structureReducer, initialLayout, (init): LayoutState => {
    const structure = init.structure ?? { groups: [] };
    const normalized: EditorLayout = { ...init, structure };
    return { layout: normalized, lastSavedLayout: normalized };
  });

  // Reset all state when navigating to a different layout (identified by id change).
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch({ type: 'reset', layout: initialLayout });
  }, [initialLayout.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasUnsavedChanges = useMemo(() => !isEqual(layout, lastSavedLayout), [layout, lastSavedLayout]);
  const modules = useMemo(() => deriveModules(layout), [layout]);
  const treatmentTypeSettings = useTreatmentTypeSettings(publicationKey);

  const { alertError } = useAlert();
  const { publishLayout: publishLayoutApi, isPublishing } = useLayoutPublish({ publicationKey });

  // Module operations — dispatch is stable so these callbacks have no deps
  const updateModule = useCallback(
    (index: number, module: FlatSlot) => dispatch({ type: 'update_module', index, module }),
    []
  );
  const reorderModules = useCallback((modules: FlatSlot[]) => dispatch({ type: 'reorder_modules', modules }), []);
  const addModule = useCallback(
    (module: FlatSlot, groupId: string | null) => dispatch({ type: 'add_module', module, groupId }),
    []
  );
  const removeModule = useCallback((index: number) => dispatch({ type: 'remove_module', index }), []);
  const restoreFromHistory = useCallback(
    (historicalLayout: EditorLayout) => dispatch({ type: 'restore', layout: historicalLayout }),
    []
  );
  const updateLayoutName = useCallback((name: string) => dispatch({ type: 'update_name', name }), []);
  const updateContentTypes = useCallback(
    (contentTypes: string[]) => dispatch({ type: 'update_content_types', contentTypes }),
    []
  );
  const addGroup = useCallback(
    (label: string, insertAfterGroupId?: string | null) => dispatch({ type: 'add_group', label, insertAfterGroupId }),
    []
  );
  const updateGroup = useCallback(
    (groupId: string, patch: Partial<StructureGroupConfig>) => dispatch({ type: 'update_group', groupId, patch }),
    []
  );
  const removeGroup = useCallback((groupId: string) => dispatch({ type: 'remove_group', groupId }), []);
  const reorderGroups = useCallback((groups: StructureGroup[]) => dispatch({ type: 'reorder_groups', groups }), []);
  const updateLastSavedState = useCallback(() => dispatch({ type: 'publish_success' }), []);

  const handlePublish = useCallback(async () => {
    const validationErrors = validateLayout(layout, groupModulesByGroupId(modules));
    if (validationErrors.length > 0) {
      const lines = validationErrors.flatMap(({ groupLabel, violations }) =>
        violations.map((v) => `${groupLabel}: ${v.message}`)
      );
      alertError(`Cannot publish — the following limits are not met:\n${lines.join('\n')}`, { wrapNewlines: true });
      return;
    }
    try {
      await publishLayoutApi(layout, artifactId);
      dispatch({ type: 'publish_success' });
    } catch {
      // error already handled and alerted in useLayoutPublish
    }
  }, [layout, modules, alertError, publishLayoutApi, artifactId]);

  const getAvailableModulesForGroup = useCallback(
    (groupId: string | null): { treatmentType: string; label: string }[] => {
      if (groupId === null) return [];
      const layoutGroup = layout.structure?.groups.find((g) => g.id === groupId);
      if (!layoutGroup) return [];

      const { allowedTreatmentTypes } = layoutGroup.config;
      const allTTs = treatmentTypeSettings
        .filter(
          (s) =>
            allowedTreatmentTypes.includes(s.treatmentType) || allowedTreatmentTypes.includes(s.treatmentTypeKey ?? '')
        )
        .map((s) => s.treatmentTypeKey ?? s.treatmentType);

      const groupModules = modules.filter((m) => m.groupId === groupId);
      if (layoutGroup.config.max != null && groupModules.length >= layoutGroup.config.max) return [];

      return allTTs.flatMap((tt) => {
        const setting = findTreatmentSetting(treatmentTypeSettings, tt);
        const currentCount = groupModules.filter((m) => m.ttKey === tt || m.ttKey === setting?.treatmentTypeKey).length;
        const limit = layoutGroup.config.treatmentTypeLimits?.find((l) => l.ref === tt);
        if (currentCount >= (limit?.max ?? Infinity)) return [];
        return [{ treatmentType: tt, label: setting?.label ?? tt }];
      });
    },
    [layout, modules, treatmentTypeSettings]
  );

  const addNewModule = useCallback((groupId: string | null, treatmentType: string, localInsertIndex?: number) => {
    if (groupId === null) return;
    // slotRef is recomputed from occurrence order at publish time; use a placeholder for in-memory tracking
    const slot: FlatSlot = { ttKey: treatmentType, slotRef: `${treatmentType}-draft`, groupId };
    dispatch({ type: 'add_module', module: slot, groupId, localInsertIndex });
  }, []);

  const value: StructureEditContextValue = {
    layout,
    modules,
    treatmentTypeSettings,
    isPublishing,
    hasUnsavedChanges,
    isReadOnly,
    updateLastSavedState,
    updateModule,
    reorderModules,
    addModule,
    removeModule,
    restoreFromHistory,
    updateLayoutName,
    updateContentTypes,
    addGroup,
    updateGroup,
    removeGroup,
    reorderGroups,
    handlePublish,
    getAvailableModulesForGroup,
    addNewModule
  };

  return <StructureEditContext.Provider value={value}>{children}</StructureEditContext.Provider>;
};
